import { useFeatureFlags } from "@partners/ui";

import { useSegmentAnalytics } from "./useSegmentAnalytics";

export const useMarketingBannersPageAnalytics = () => {
  const {
    trackButtonClickEvent: baseTrackButtonClickEvent,
    trackOptionClickEvent: baseTrackOptionClickEvent,
    trackInputChangeEvent: baseTrackInputChangeEvent,
    trackPageEvent: baseTrackPageEvent,
  } = useSegmentAnalytics();

  // Kill-switch (PART-3100): while the flag is off, every Marketing Banners /
  // Banner Library event is a no-op. Each noop is typed to its base tracker so
  // the gated function keeps the exact callable signature.
  const { marketingBannerSegmentTracking } = useFeatureFlags();
  const noopButtonClickEvent: typeof baseTrackButtonClickEvent = () => {};
  const noopOptionClickEvent: typeof baseTrackOptionClickEvent = () => {};
  const noopInputChangeEvent: typeof baseTrackInputChangeEvent = () => {};
  const noopPageEvent: typeof baseTrackPageEvent = () => {};
  const trackButtonClickEvent = marketingBannerSegmentTracking
    ? baseTrackButtonClickEvent
    : noopButtonClickEvent;
  const trackOptionClickEvent = marketingBannerSegmentTracking
    ? baseTrackOptionClickEvent
    : noopOptionClickEvent;
  const trackInputChangeEvent = marketingBannerSegmentTracking
    ? baseTrackInputChangeEvent
    : noopInputChangeEvent;
  const trackPageEvent = marketingBannerSegmentTracking
    ? baseTrackPageEvent
    : noopPageEvent;

  // AC-1 — page-view, fired from the tab controller (not analyticsPageMap,
  // since the Referral links / Marketing banners tabs share one pathname).
  const trackPageViewEvent = () =>
    trackPageEvent({
      name: "Marketing banners",
      properties: {
        event_type: "Page loaded",
      },
    });

  // A1
  const trackSearchInputEvent = (value: string) =>
    trackInputChangeEvent({
      label: "Marketing Banners - Search Text box",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Text input",
        link_text: "Search field",
        input_value: value,
      },
    });

  // A2
  const trackFilterButtonEvent = () =>
    trackButtonClickEvent({
      label: "Marketing Banners - Filter button",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing banners - Filter button",
      },
    });

  // A3 — sheet's literal link_text ("...Create new link button") is a
  // copy-paste artifact; using the banner-correct text, see
  // PART-3099-resources/PART-3099-segment-sheet.md row A3.
  const trackCreateNewBannerEvent = () =>
    trackButtonClickEvent({
      label: "Marketing Banners - Create new banner",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing banners - Create new banner button",
      },
    });

  // A4
  const trackExportCsvEvent = () =>
    trackButtonClickEvent({
      label: "Marketing Banners - Export CSV button",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing banners - Export CSV button",
      },
    });

  // A5
  const trackCopyIconEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - Copy icon",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} of the banner copied`,
      },
    });

  // A6
  const trackViewPerformanceIconEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing banners - View Performance icon",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} of the Performance icon clicked`,
      },
    });

  // A7
  const trackMoreMenuEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - More menu",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} for which More menu is clicked`,
      },
    });

  // A8
  const trackMoreMenuViewBannerEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - More menu - View banner",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} of the view banner clicked`,
      },
    });

  // A9
  const trackMoreMenuCopyHtmlCodeEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - More menu - Copy HTML code",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} of the Copy HTML code clicked`,
      },
    });

  // A10
  const trackMoreMenuRenameEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - More menu - Rename",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} for which rename is clicked`,
      },
    });

  // A11 — only fires for image banners, gated at the callsite.
  const trackMoreMenuDownloadPngEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - More menu - Download as PNG",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} for which Download as PNG is clicked`,
      },
    });

  // A12 — same label text as A9, distinct location surface (fired from
  // inside the View Banner preview modal, not the row's more-menu).
  const trackViewBannerModalCopyHtmlCodeEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label:
        "Marketing Banners - More menu - View Banner modal - Copy HTML code button",
      location: "Marketing banners",
      extraProperties: {
        event_type: "Click",
        link_text: `${bannerName} of the Copy HTML code clicked`,
      },
    });

  // A13
  const trackFilterBannerSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Marketing Banners - Filter - Banner",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // A14
  const trackFilterLanguageSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Marketing Banners - Filter - Language selector",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // A15 — corrected to "Option clicked" per staging QA (PART-3099,
  // 2026-09-09): the sheet's literal "Click" event_type didn't match the
  // MultiSelect facet's actual behaviour.
  const trackFilterBannerSizeSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Marketing Banners - Filter - Banner size",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // A16
  const trackFilterTypeEvent = (value: string) =>
    trackOptionClickEvent({
      label: "Marketing Banners - Filter - Type",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: value,
      },
    });

  // A17 — corrected to "Option clicked" per staging QA (PART-3099,
  // 2026-09-09), same correction as A15.
  const trackFilterRebateProgramSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Marketing Banners - Filter - Rebate program selector",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackFilterCampaignSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Marketing banners - Filter - Campaign",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // A18
  const trackFilterApplyEvent = (selection: {
    banners: string[];
    languages: string[];
    sizes: string[];
    types: string[];
    rebatePrograms: string[];
  }) =>
    trackButtonClickEvent({
      label: "Marketing Banners - Filter - Apply",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: [
          selection.banners,
          selection.languages,
          selection.sizes,
          selection.types,
          selection.rebatePrograms,
        ]
          .filter((values) => values.length > 0)
          .map((values) => values.join(","))
          .join(" + "),
      },
    });

  // A19
  const trackFilterClearEvent = () =>
    trackButtonClickEvent({
      label: "Marketing Banners - Filter - Clear",
      location: "Marketing banners - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing banners - Filter - Clear",
      },
    });

  // A20
  const trackRenameSaveEvent = (bannerName: string) =>
    trackButtonClickEvent({
      label: "Marketing Banners - Rename banner modal - Save button",
      location: "Marketing banners - Rename",
      extraProperties: {
        event_type: "Click",
        link_text: bannerName,
      },
    });

  // A21
  const trackRenameCancelEvent = () =>
    trackButtonClickEvent({
      label: "Marketing Banners - Rename banner modal - Cancel button",
      location: "Marketing banners - Rename",
      extraProperties: {
        event_type: "Click",
        link_text: "Marketing banners - Rename banner modal - Cancel button",
      },
    });

  // B1
  const trackBannerLibraryAllTabEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - All tab",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - All tab",
      },
    });

  // B2
  const trackBannerLibraryHtmlTabEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - HTML tab",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - HTML tab",
      },
    });

  // B2a — Image tab; mirrors the All/HTML tab tracking (staging QA, 2026-09-09).
  const trackBannerLibraryImageTabEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - Image tab",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - Image tab",
      },
    });

  // B3
  const trackBannerLibraryFilterButtonEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - Filter button",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - Filter button",
      },
    });

  // B4
  const trackBannerLibraryLanguageListTooltipEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - Language list tooltip",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - Language list tooltip",
      },
    });

  // B5
  const trackBannerLibrarySizeListTooltipEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - Size list tooltip",
      location: "Banner library",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - Size list tooltip",
      },
    });

  // B6 — sheet's event_type is "Option clicked" with a static link_text
  // (unlike genuine Option-clicked rows, which all carry a <...selected>
  // placeholder) — flagged as a likely sheet inconsistency in
  // PART-3099-resources/PART-3099-segment-sheet.md row B6, but implemented
  // per the sheet's and AC-3's literal event_type (no tracking-plan-owner
  // approval to deviate from the documented contract).
  const trackBannerLibraryConfigureAssetButtonEvent = () =>
    trackOptionClickEvent({
      label: "Banner library - Configure asset button",
      location: "Banner library",
      extraProperties: {
        event_type: "Option clicked",
        link_text: "Banner library - Configure asset button",
      },
    });

  // B7
  const trackBannerLibraryFilterLanguageEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Banner library - Filter - Language",
      location: "Banner library - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // B8 — corrected to "Option clicked" per staging QA (2026-09-09): the sheet's
  // literal "Click"/Button category didn't match the MultiSelect facet's actual
  // behaviour, same correction as A15/A17.
  const trackBannerLibraryFilterSizeEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Banner library - Filter - Banner size",
      location: "Banner library - Filter",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // B9
  const trackBannerLibraryFilterApplyEvent = (selection: {
    languages: string[];
    sizes: string[];
  }) =>
    trackButtonClickEvent({
      label: "Banner library - Filter - Apply button",
      location: "Banner library - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: [selection.languages, selection.sizes]
          .filter((values) => values.length > 0)
          .map((values) => values.join(","))
          .join(" + "),
      },
    });

  // B10
  const trackBannerLibraryFilterClearEvent = () =>
    trackButtonClickEvent({
      label: "Banner library - Filter - Clear button",
      location: "Banner library - Filter",
      extraProperties: {
        event_type: "Click",
        link_text: "Banner library - Filter - Clear button",
      },
    });

  // C1
  const trackConfigureAssetsRebateProgramDropdownEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Configure assets - Rebate program dropdown",
      location: "Configure assets",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  const trackConfigureAssetsCampaignSelectorEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Configure asset - campaign selector",
      location: "Configure assets",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // C2
  const trackConfigureAssetsTypeTileEvent = (value: string) =>
    trackButtonClickEvent({
      label: "Configure assets - Type tile",
      location: "Configure assets",
      extraProperties: {
        event_type: "Click",
        link_text: value,
      },
    });

  // C3
  const trackConfigureAssetsSizeTileEvent = (value: string) =>
    trackButtonClickEvent({
      label: "Configure assets - Size tile",
      location: "Configure assets",
      extraProperties: {
        event_type: "Click",
        link_text: value,
      },
    });

  // C4
  const trackConfigureAssetsLanguageDropdownEvent = (option: string) =>
    trackOptionClickEvent({
      label: "Configure assets - Language dropdown",
      location: "Configure assets",
      extraProperties: {
        event_type: "Option clicked",
        link_text: option,
      },
    });

  // C5
  const trackConfigureAssetsNameEvent = (value: string) =>
    trackInputChangeEvent({
      label: "Configure assets - Name",
      location: "Configure assets",
      extraProperties: {
        event_type: "Text input",
        link_text: value,
        input_value: value,
      },
    });

  // C6
  const trackConfigureAssetsCreateBannerEvent = (selection: {
    rebateProgram: string;
    type: string;
    size: string;
    language: string;
    name: string;
  }) =>
    trackButtonClickEvent({
      label: "Configure assets - Create banner",
      location: "Configure assets",
      extraProperties: {
        event_type: "Click",
        link_text: [
          selection.rebateProgram,
          selection.type,
          selection.size,
          selection.language,
          selection.name,
        ].join(" + "),
      },
    });

  return {
    trackPageViewEvent,
    trackSearchInputEvent,
    trackFilterButtonEvent,
    trackCreateNewBannerEvent,
    trackExportCsvEvent,
    trackCopyIconEvent,
    trackViewPerformanceIconEvent,
    trackMoreMenuEvent,
    trackMoreMenuViewBannerEvent,
    trackMoreMenuCopyHtmlCodeEvent,
    trackMoreMenuRenameEvent,
    trackMoreMenuDownloadPngEvent,
    trackViewBannerModalCopyHtmlCodeEvent,
    trackFilterBannerSelectorEvent,
    trackFilterLanguageSelectorEvent,
    trackFilterBannerSizeSelectorEvent,
    trackFilterTypeEvent,
    trackFilterRebateProgramSelectorEvent,
    trackFilterCampaignSelectorEvent,
    trackFilterApplyEvent,
    trackFilterClearEvent,
    trackRenameSaveEvent,
    trackRenameCancelEvent,
    trackBannerLibraryAllTabEvent,
    trackBannerLibraryHtmlTabEvent,
    trackBannerLibraryImageTabEvent,
    trackBannerLibraryFilterButtonEvent,
    trackBannerLibraryLanguageListTooltipEvent,
    trackBannerLibrarySizeListTooltipEvent,
    trackBannerLibraryConfigureAssetButtonEvent,
    trackBannerLibraryFilterLanguageEvent,
    trackBannerLibraryFilterSizeEvent,
    trackBannerLibraryFilterApplyEvent,
    trackBannerLibraryFilterClearEvent,
    trackConfigureAssetsRebateProgramDropdownEvent,
    trackConfigureAssetsCampaignSelectorEvent,
    trackConfigureAssetsTypeTileEvent,
    trackConfigureAssetsSizeTileEvent,
    trackConfigureAssetsLanguageDropdownEvent,
    trackConfigureAssetsNameEvent,
    trackConfigureAssetsCreateBannerEvent,
  };
};
